import { useCallback, useEffect, useState, useTransition } from 'react';
import { IMessage } from '../screens/Prompt/Prompt';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  experimental_streamedQuery as streamedQuery,
} from '@tanstack/react-query';
import { getChatConfiguration } from '../api/chat';
import { setChatConfiguration } from '../api/chat';
import { ChatConfiguration } from '../api/chat';
import { httpsCallable, HttpsCallableResult } from 'firebase/functions';
import { functions } from '../firebase';
import { useFormik } from 'formik';

interface PromptResponse {
  id: string;
  text: string;
}

export function useChat() {
  const [messages, setMessages] = useState<IMessage[]>([]);
  const queryClient = useQueryClient();
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const { data: chatConfig } = useQuery({
    queryKey: ['chat-config'],
    queryFn: getChatConfiguration,
  });

  const { mutate: saveChatConfig, isPending: isSaving } = useMutation({
    mutationKey: ['save-chat-config'],
    mutationFn: async (config: ChatConfiguration) => {
      const result = await setChatConfiguration(config);
      if (result) {
        throw new Error('Failed to save chat configuration');
      } else {
        queryClient.invalidateQueries({ queryKey: ['chat-config'] });
      }
    },
    onSuccess: () => {
      setNotification({
        open: true,
        message: 'Chat configuration saved successfully',
        severity: 'success',
      });
    },
    onError: () => {
      setNotification({
        open: true,
        message: 'Failed to save chat configuration. Please try again.',
        severity: 'error',
      });
    },
  });

  const form = useFormik({
    initialValues: {
      systemPrompt: chatConfig?.prompt || '',
      temperature: chatConfig?.temperature || 0.7,
      message: '',
    },
    onSubmit: async (values, form) => {
      form.setFieldValue('message', '');
      const message = { id: crypto.randomUUID(), text: values.message, sender: 'user' } satisfies IMessage;
      setMessages((prev) => [...prev, message]);
      form.setStatus(crypto.randomUUID());
    },
  });

  const handleCloseNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  const clearChat = useCallback(() => {
    setMessages([]);
  }, []);

  useEffect(() => {
    if (chatConfig) {
      form.setFieldValue('systemPrompt', chatConfig.prompt || '');
      form.setFieldValue('temperature', chatConfig.temperature || 0.7);
    }
  }, [chatConfig]);

  return {
    messages,
    notification,
    handleCloseNotification,
    form,
    saveChatConfig,
    isSaving,
    clearChat,
    systemPrompt: chatConfig?.prompt || '',
    setNotification,
    setMessages,
  };
}
